import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { authAxios } from "../../utils/auth";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Shield, RefreshCw, AlertTriangle, ArrowUpDown, FileWarning } from "lucide-react";
import { toast } from "sonner";
import AdminMenu from "../../components/AdminMenu";

const KIND_LABEL = {
  card_fp: "Duplicate card fingerprint cluster",
  device_fp: "Shared device fingerprint cluster",
  ip_range: "IP-range cluster",
  recurring_buyer: "Recurring buyer pattern",
};

const STATUS_TONE = {
  active: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
  warning: "text-amber-300 bg-amber-500/10 border-amber-500/30",
  frozen: "text-red-300 bg-red-500/10 border-red-500/30",
  blocked: "text-red-200 bg-red-600/20 border-red-600/40",
};

// Reuses the same palette Guardian uses everywhere else so admins get
// one consistent visual language for the fraud_status field.
const FRAUD_STATUS_TONE = {
  clean: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
  watch: "text-white/70 bg-white/10 border-white/20",
  prewarn: "text-amber-300 bg-amber-500/20 border-amber-500/40",
  freeze: "text-red-400 bg-red-500/20 border-red-500/40",
  confirmed_fraud: "text-red-300 bg-red-900/40 border-red-700",
};

/**
 * /admin/fraud/cluster/:kind/:key — the drill-down page opened from the
 * Guardian threat summary. Lists every user in the cluster with per-row
 * status controls and a bulk "apply to selected" action.
 */
export default function ClusterDetail() {
  const { kind, key } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState({});
  const [bulkStatus, setBulkStatus] = useState("");
  const [bulkReason, setBulkReason] = useState("");
  const [working, setWorking] = useState(false);
  const [sortBy, setSortBy] = useState("account_status");
  const [sortDir, setSortDir] = useState("desc");

  const load = async () => {
    setLoading(true);
    try {
      const r = await authAxios.get(`/admin/fraud/cluster/${kind}/${encodeURIComponent(key)}`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load cluster");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind, key]);

  const users = data?.users || [];
  const sortedUsers = [...users].sort((a, b) => {
    const av = a[sortBy] ?? "";
    const bv = b[sortBy] ?? "";
    if (typeof av === "number" && typeof bv === "number") {
      return sortDir === "asc" ? av - bv : bv - av;
    }
    return sortDir === "asc"
      ? String(av).localeCompare(String(bv))
      : String(bv).localeCompare(String(av));
  });
  const setSort = (k) => {
    if (k === sortBy) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortBy(k); setSortDir("desc"); }
  };
  const allSelected = users.length > 0 && users.every((u) => selected[u.user_id]);
  const anySelected = users.some((u) => selected[u.user_id]);

  const toggleAll = () => {
    if (allSelected) setSelected({});
    else setSelected(Object.fromEntries(users.map((u) => [u.user_id, true])));
  };

  const applyRow = async (uid, status) => {
    if (!status) return;
    const reason = window.prompt(
      `Set account_status="${status}" for user ${uid}?\n\nEnter a short reason (visible in Guardian timeline):`,
      "cluster review",
    );
    if (reason === null) return;
    try {
      await authAxios.post(`/admin/fraud/user/${uid}/status`, { status, reason });
      toast.success(`${uid.slice(0, 12)}… → ${status}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const applyBulk = async () => {
    if (!bulkStatus || working) return;
    const ids = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
    if (!ids.length) { toast.error("Select at least one user"); return; }
    if (!window.confirm(
      `Set account_status="${bulkStatus}" on ${ids.length} user${ids.length !== 1 ? "s" : ""}?\n\nAll listed side-effects will fire (commissions frozen, subs paused where applicable).`
    )) return;
    setWorking(true);
    try {
      const r = await authAxios.post("/admin/fraud/users/bulk-status", {
        user_ids: ids, status: bulkStatus, reason: bulkReason || "cluster bulk action",
      });
      toast.success(`Applied to ${r.data.ok}/${r.data.count} · ${r.data.failed} failed`);
      setSelected({});
      setBulkStatus("");
      setBulkReason("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk failed");
    } finally {
      setWorking(false);
    }
  };

  return (
    <div className="min-h-screen bg-divine-black text-white">
      <header className="border-b border-white/10 bg-divine-black-light/70 backdrop-blur sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => navigate("/admin/analytics?tab=guardian")}
              className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/5"
              data-testid="cluster-detail-back"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <Shield className="w-4 h-4 text-red-400" />
                <h1 className="text-sm font-semibold truncate">
                  {KIND_LABEL[kind] || kind}
                </h1>
              </div>
              <p className="text-xs text-white/40 font-mono truncate">key: {key}</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={load}
              className="p-2 rounded-lg text-white/50 hover:text-white hover:bg-white/10"
              data-testid="cluster-detail-refresh"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? "animate-spin" : ""}`} />
            </button>
            <AdminMenu />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        {/* Cluster summary */}
        <Card className="bg-red-500/[0.05] border-red-500/20">
          <CardHeader className="py-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-400" />
              Cluster Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="text-xs text-white/70 space-y-1">
            <div>Kind: <span className="text-white/90 font-mono">{kind}</span></div>
            <div>Key: <span className="text-white/90 font-mono">{key}</span></div>
            <div>Users in cluster: <span className="text-red-300 font-semibold">{users.length}</span></div>
            {data?.summary && Object.entries(data.summary).map(([k, v]) => (
              <div key={k}>{k}: <span className="text-white/90 font-mono">{JSON.stringify(v)}</span></div>
            ))}
          </CardContent>
        </Card>

        {/* Bulk action bar */}
        <div className="rounded-lg border border-white/10 bg-divine-black-light/50 p-3 flex flex-wrap items-center gap-2">
          <span className="text-xs text-white/50 uppercase tracking-wider">
            {anySelected ? `${Object.values(selected).filter(Boolean).length} selected` : "Bulk edit"}
          </span>
          <Select value={bulkStatus} onValueChange={setBulkStatus}>
            <SelectTrigger className="w-40 h-8 text-xs" data-testid="cluster-bulk-status">
              <SelectValue placeholder="Set status…" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">ACTIVE</SelectItem>
              <SelectItem value="warning">WARNING</SelectItem>
              <SelectItem value="frozen">FREEZE</SelectItem>
              <SelectItem value="blocked">BLOCK</SelectItem>
            </SelectContent>
          </Select>
          <input
            type="text"
            placeholder="Reason (optional)"
            value={bulkReason}
            onChange={(e) => setBulkReason(e.target.value)}
            className="h-8 px-2 text-xs bg-white/5 border border-white/10 rounded flex-1 min-w-[180px]"
            data-testid="cluster-bulk-reason"
          />
          <Button
            onClick={applyBulk}
            disabled={!bulkStatus || !anySelected || working}
            className="h-8 text-xs"
            data-testid="cluster-bulk-apply"
          >
            {working ? "Applying…" : "Apply to Selected"}
          </Button>
        </div>

        {/* Users table */}
        <Card className="bg-divine-black-light/40 border-white/10">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-10 text-center text-white/50 text-sm">Loading…</div>
            ) : users.length === 0 ? (
              <div className="p-10 text-center text-white/50 text-sm">No users found in this cluster.</div>
            ) : (
              <Table data-testid="cluster-users-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-8">
                      <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="cluster-select-all" />
                    </TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>
                      <button onClick={() => setSort("account_status")} className="inline-flex items-center gap-1 hover:text-white" data-testid="sort-account-status">
                        Account Status <ArrowUpDown className="w-3 h-3 opacity-60" />
                      </button>
                    </TableHead>
                    <TableHead>
                      <button onClick={() => setSort("fraud_status")} className="inline-flex items-center gap-1 hover:text-white" data-testid="sort-fraud-status">
                        Fraud Status <ArrowUpDown className="w-3 h-3 opacity-60" />
                      </button>
                    </TableHead>
                    <TableHead>Cases</TableHead>
                    <TableHead>Signup</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedUsers.map((u) => (
                    <TableRow key={u.user_id} data-testid={`cluster-row-${u.user_id}`}>
                      <TableCell>
                        <Checkbox
                          checked={!!selected[u.user_id]}
                          onCheckedChange={(v) => setSelected((s) => ({ ...s, [u.user_id]: !!v }))}
                          data-testid={`cluster-select-${u.user_id}`}
                        />
                      </TableCell>
                      <TableCell>
                        <Link
                          to={`/admin/fraud/defender/${u.user_id}`}
                          className="text-divine-gold hover:underline"
                          data-testid={`cluster-user-link-${u.user_id}`}
                        >
                          {u.name || u.user_id.slice(0, 12) + "…"}
                        </Link>
                      </TableCell>
                      <TableCell className="text-xs text-white/70 font-mono">{u.email || "—"}</TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center px-2 py-0.5 text-[10px] rounded uppercase tracking-wider border ${STATUS_TONE[u.account_status] || STATUS_TONE.active}`}>
                          {(u.account_status || "active").toUpperCase()}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className={`inline-flex items-center px-2 py-0.5 text-[10px] rounded uppercase tracking-wider border ${FRAUD_STATUS_TONE[u.fraud_status] || FRAUD_STATUS_TONE.clean}`}
                              data-testid={`cluster-fraud-${u.user_id}`}>
                          {(u.fraud_status || "clean").replace("_", " ")}
                        </span>
                      </TableCell>
                      <TableCell>
                        {u.cases_count > 0 ? (
                          <Link
                            to={`/admin/fraud/defender/${u.user_id}`}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-red-500/40 text-[10px] text-red-300 hover:bg-red-500/10"
                            data-testid={`cluster-cases-${u.user_id}`}
                          >
                            <FileWarning className="w-3 h-3" /> {u.cases_count}
                          </Link>
                        ) : (
                          <span className="text-xs text-white/30">0</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-white/40 font-mono">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        <Select onValueChange={(v) => applyRow(u.user_id, v)}>
                          <SelectTrigger className="h-7 w-32 text-[11px]" data-testid={`cluster-row-action-${u.user_id}`}>
                            <SelectValue placeholder="Set status…" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="active">ACTIVE</SelectItem>
                            <SelectItem value="warning">WARNING</SelectItem>
                            <SelectItem value="frozen">FREEZE</SelectItem>
                            <SelectItem value="blocked">BLOCK</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
